/**
 * VerifyCode - 6-digit code verification page
 * Route: /i/:token/verify
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyCode, parseRpcError } from '@shared/utils/publicRpc';
import { useInviteToken, buildInvitePath } from '@shared/utils/inviteToken';

export default function VerifyCode() {
  const token = useInviteToken();
  const navigate = useNavigate();
  
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const isValid = await verifyCode(token, code);

      if (isValid) {
        // Code verified - proceed to induction
        navigate(buildInvitePath(token, 'induction'));
      } else {
        setError('Invalid verification code. Please check your email and try again.');
      }
    } catch (err) {
      setError(parseRpcError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError(null);
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Verify Your Identity</h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter the 6-digit verification code sent to your email
          </p>
        </div>

        {/* Verification form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              className="block w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              maxLength={6}
              autoComplete="off"
              autoFocus
              disabled={loading}
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
              Enter the 6-digit code from your email
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </button>

          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate(buildInvitePath(token))}
            className="w-full text-sm text-gray-600 hover:text-gray-900"
          >
            ← Back to invite
          </button>
        </form>

        {/* Help text */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Didn't receive a code?{' '}
            <a href="mailto:support@example.com" className="text-blue-600 hover:text-blue-700">
              Contact support
            </a>
          </p>
          <p className="mt-2 text-xs text-gray-500 text-center">
            Note: After 5 failed attempts, your invite will be locked for 15 minutes
          </p>
        </div>
      </div>
    </div>
  );
}
